import React, { useState, useEffect, useCallback } from 'react';
import styles from './CookieConsent.module.css';
import AvisoPrivacidadModal from '../Avisoprivacidadmodal/index';

// ─── Constantes ──────────────────────────────────────────
const COOKIE_CONSENT_KEY = 'mccormick_cookie_preferences';

const DEFAULT_PREFERENCES = {
    essential: true,
    gtm: false,
    analytics: false,
    crazyegg: false,
};

// ─── Helpers públicos ────────────────────────────────────
/** Devuelve las preferencias guardadas o null si el usuario aún no decide. */
export function getCookiePreferences() {
    try {
        const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

/** Indica si una categoría específica está aceptada. */
export function isCookieAccepted(category) {
    const prefs = getCookiePreferences();
    if (!prefs) return false;
    return !!prefs[category];
}

/** Evento personalizado que se dispara cuando el usuario guarda sus preferencias. */
export const CONSENT_CHANGED_EVENT = 'mccormick:consent-changed';

function dispatchConsentChanged(prefs) {
    window.dispatchEvent(
        new CustomEvent(CONSENT_CHANGED_EVENT, { detail: prefs })
    );
}

// ─── Inyección dinámica de scripts ───────────────────────
function injectGTM() {
    if (document.getElementById('mcck-gtm-script')) return;
    const script = document.createElement('script');
    script.id = 'mcck-gtm-script';
    script.async = true;
    script.textContent = `
    (function(w,d,s,l,i){
      w[l]=w[l]||[];
      w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
      var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s), dl=l!='dataLayer'?'&l='+l:'';
      j.async=true;
      j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
      f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','GTM-NSFVQT8');
  `;
    document.head.appendChild(script);
}

function injectGA() {
    if (document.getElementById('mcck-ga-script')) return;
    // gtag.js loader
    const loader = document.createElement('script');
    loader.id = 'mcck-ga-script';
    loader.async = true;
    loader.src = 'https://www.googletagmanager.com/gtag/js?id=UA-160658427-1';
    document.head.appendChild(loader);
    // gtag init
    const init = document.createElement('script');
    init.id = 'mcck-ga-init';
    init.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'UA-160658427-1');
  `;
    document.head.appendChild(init);
}

function injectCrazyEgg() {
    if (document.getElementById('mcck-crazyegg-script')) return;
    const script = document.createElement('script');
    script.id = 'mcck-crazyegg-script';
    script.type = 'text/javascript';
    script.async = true;
    script.src = '//script.crazyegg.com/pages/scripts/0024/9558.js';
    document.head.appendChild(script);
}

/** Inyecta scripts según las preferencias guardadas. Llamar al montar la app. */
export function loadConsentedScripts() {
    const prefs = getCookiePreferences();
    if (!prefs) return;
    if (prefs.gtm) injectGTM();
    if (prefs.analytics) injectGA();
    if (prefs.crazyegg) injectCrazyEgg();
}

// ─── Definición de categorías para el UI ─────────────────
const CATEGORIES = [
    {
        key: 'essential',
        label: 'Esenciales',
        description: 'Necesarias para el funcionamiento básico del sitio.',
        locked: true,
    },
    {
        key: 'gtm',
        label: 'Google Tag Manager',
        description: 'Etiquetas de marketing y personalización (GTM-NSFVQT8).',
    },
    {
        key: 'analytics',
        label: 'Google Analytics',
        description: 'Estadísticas anónimas de uso del sitio (UA-160658427-1).',
    },
    {
        key: 'crazyegg',
        label: 'Crazy Egg',
        description: 'Mapas de calor y análisis de comportamiento.',
    },
];

// ─── Componente ──────────────────────────────────────────
const CookieConsent = () => {
    const [visible, setVisible] = useState(false);
    const [showAviso, setShowAviso] = useState(false);
    const [preferences, setPreferences] = useState({ ...DEFAULT_PREFERENCES });

    useEffect(() => {
        const saved = getCookiePreferences();
        if (!saved) {
            setVisible(true);
        } else {
            setPreferences(saved);
        }
    }, []);

    const save = useCallback((prefs) => {
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
        setPreferences(prefs);
        setVisible(false);
        dispatchConsentChanged(prefs);
        // Inyectar scripts inmediatamente tras aceptar
        if (prefs.gtm) injectGTM();
        if (prefs.analytics) injectGA();
        if (prefs.crazyegg) injectCrazyEgg();
    }, []);

    const handleAcceptAll = () => {
        save({ essential: true, gtm: true, analytics: true, crazyegg: true });
    };

    const handleRejectAll = () => {
        save({ ...DEFAULT_PREFERENCES });
    };

    const handleSaveSelection = () => {
        save({ ...preferences, essential: true });
    };

    const toggle = (key) => {
        if (key === 'essential') return;
        setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    if (!visible) return null;

    return (
        <>
            <div className={styles.overlay} />
            <div
                className={styles.banner}
                role="dialog"
                aria-label="Consentimiento de cookies"
            >
                <div className={styles.container}>
                    {/* ── Encabezado ── */}
                    <div className={styles.header}>
                        <span className={styles.icon} aria-hidden="true">🍪</span>
                        <div>
                            <h3 className={styles.title}>Configuración de cookies</h3>
                            <p className={styles.subtitle}>
                                Elige qué cookies permitir.{' '}
                                <a
                                    role="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowAviso(true);
                                    }}
                                >
                                    Aviso de Privacidad
                                </a>
                            </p>
                        </div>
                    </div>

                    {/* ── Categorías ── */}
                    <div className={styles.categories}>
                        {CATEGORIES.map((cat) => {
                            const checked = preferences[cat.key];
                            const sliderClasses = [
                                styles.toggleSlider,
                                checked ? styles.toggleChecked : '',
                                cat.locked && !checked ? styles.toggleDisabled : '',
                                cat.locked && checked ? styles.toggleDisabledChecked : '',
                            ].filter(Boolean).join(' ');

                            return (
                                <label
                                    key={cat.key}
                                    className={`${styles.category} ${cat.locked ? styles.locked : ''}`}
                                >
                                    <div className={styles.categoryInfo}>
                                        <span className={styles.categoryLabel}>{cat.label}</span>
                                        <span className={styles.categoryDesc}>{cat.description}</span>
                                    </div>
                                    <div className={styles.toggleWrapper}>
                                        <input
                                            type="checkbox"
                                            className={styles.toggleInput}
                                            checked={checked}
                                            disabled={cat.locked}
                                            onChange={() => toggle(cat.key)}
                                        />
                                        <span className={sliderClasses} />
                                    </div>
                                </label>
                            );
                        })}
                    </div>

                    {/* ── Botones ── */}
                    <div className={styles.buttons}>
                        <button className={styles.btnAccept} onClick={handleAcceptAll}>
                            Aceptar todas
                        </button>
                        <button className={styles.btnSave} onClick={handleSaveSelection}>
                            Guardar selección
                        </button>
                        <button className={styles.btnReject} onClick={handleRejectAll}>
                            Rechazar todas
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal Aviso de Privacidad */}
            <AvisoPrivacidadModal
                modal={showAviso}
                handleModal={() => setShowAviso(false)}
            />
        </>
    );
};

export default CookieConsent;
